/**
 * TIMELESS DISPLAY — Custom Product Page JavaScript
 * Handles all product page interactivity
 */

class ProductCustom {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.section;
    this.productId = container.dataset.productId;

    // Elements
    this.mainImage = container.querySelector('.product-custom__featured-image');
    this.mainImageWrapper = container.querySelector('.product-custom__main-image');
    this.thumbnails = container.querySelectorAll('.product-custom__thumbnail');
    this.thumbnailsTrack = container.querySelector('.product-custom__thumbnails-track');
    this.thumbnailsWrapper = container.querySelector('.product-custom__thumbnails');
    this.prevNav = container.querySelector('.product-custom__thumbnail-nav--prev');
    this.nextNav = container.querySelector('.product-custom__thumbnail-nav--next');

    // Personalisation elements
    this.personalisationToggle = container.querySelector(`#PersonalisationToggle-${this.sectionId}`);
    this.personalisationCollapsed = container.querySelector(`#PersonalisationCollapsed-${this.sectionId}`);
    this.personalisationExpanded = container.querySelector(`#PersonalisationExpanded-${this.sectionId}`);
    this.personalisationRemove = container.querySelector(`#PersonalisationRemove-${this.sectionId}`);
    this.personalisationInput = container.querySelector('.product-custom__personalisation-input');

    // Variant elements
    this.variantInputs = container.querySelectorAll('.product-custom__swatch-input, .product-custom__variant-select');
    this.priceElement = container.querySelector(`#Price-${this.sectionId}`);
    this.variantIdInput = container.querySelector('.product-variant-id');
    this.submitButton = container.querySelector('.product-custom__submit-btn');

    // Quantity elements
    this.quantityInput = container.querySelector('.product-custom__quantity-input');
    this.minusBtn = container.querySelector('.product-custom__quantity-btn[name="minus"]');
    this.plusBtn = container.querySelector('.product-custom__quantity-btn[name="plus"]');

    // Load variant data
    this.variants = this.loadVariants();

    // Initialize
    this.init();
  }

  init() {
    this.bindThumbnailEvents();
    this.bindThumbnailNavigation();
    this.bindPersonalisationEvents();
    this.bindVariantEvents();
    this.bindQuantityEvents();
    this.bindFormSubmit();
  }

  /**
   * Load product variants from JSON
   */
  loadVariants() {
    const variantsScript = this.container.querySelector(`#ProductVariants-${this.sectionId}`);
    if (variantsScript) {
      try {
        return JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant data:', e);
        return [];
      }
    }
    return [];
  }

  /**
   * THUMBNAIL GALLERY
   */
  bindThumbnailEvents() {
    if (!this.thumbnails.length) return;

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchImage(thumbnail);
      });
    });
  }

  switchImage(thumbnail) {
    const newSrc = thumbnail.dataset.mediaSrc;
    if (!newSrc || !this.mainImage) return;

    // Update active state
    this.thumbnails.forEach((t) => {
      t.classList.remove('product-custom__thumbnail--active');
      t.setAttribute('aria-current', 'false');
    });
    thumbnail.classList.add('product-custom__thumbnail--active');
    thumbnail.setAttribute('aria-current', 'true');

    // Instant switch
    this.mainImage.src = newSrc;

    // Optional: Add a subtle flash effect if needed, but keeping it snappy is better
    this.mainImage.classList.remove('fade-in');
    void this.mainImage.offsetWidth; // Trigger reflow
    this.mainImage.classList.add('fade-in');
  }

  bindThumbnailNavigation() {
    if (!this.prevNav || !this.nextNav || !this.thumbnailsWrapper) return;

    const thumbnailWidth = 88; // 80px + 8px gap
    const visibleCount = 5;

    this.prevNav.addEventListener('click', () => {
      this.thumbnailsWrapper.scrollBy({
        left: -thumbnailWidth * visibleCount,
        behavior: 'smooth'
      });
    });

    this.nextNav.addEventListener('click', () => {
      this.thumbnailsWrapper.scrollBy({
        left: thumbnailWidth * visibleCount,
        behavior: 'smooth'
      });
    });

    // Update nav button states on scroll
    this.thumbnailsWrapper.addEventListener('scroll', () => {
      this.updateNavButtons();
    });

    // Initial state
    this.updateNavButtons();
  }

  updateNavButtons() {
    if (!this.thumbnailsWrapper || !this.prevNav || !this.nextNav) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.thumbnailsWrapper;
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.prevNav.disabled = isAtStart;
    this.nextNav.disabled = isAtEnd;
  }

  /**
   * PERSONALISATION MODULE (Enhanced)
   */
  bindPersonalisationEvents() {
    const container = this.container.querySelector(`#Personalisation-${this.sectionId}`);
    if (!container) return;

    // Elements
    this.personalisationContainer = container;
    this.personalisationCheckbox = container.querySelector(`#PersonalisationToggle-${this.sectionId}`);
    this.personalisationExpanded = container.querySelector(`#PersonalisationExpanded-${this.sectionId}`);
    this.personalisationHiddenInput = container.querySelector(`#PersonalisationActive-${this.sectionId}`);

    // Form inputs
    this.engravingInput = container.querySelector('[data-engraving-input]');
    this.personalisationInputs = container.querySelectorAll('[data-personalisation-input]');
    this.engravingError = container.querySelector(`#EngravingTextError-${this.sectionId}`);
    this.engravingHelper = container.querySelector(`#EngravingTextHelper-${this.sectionId}`);

    // Read mode configuration from container data attributes
    this.engravingMode = (this.personalisationContainer && this.personalisationContainer.dataset.engravingMode) || 'standard';
    this.engravingMax  = parseInt((this.personalisationContainer && this.personalisationContainer.dataset.engravingMax) || '3', 10);

    // Validation regex — laser mode allows letters, digits, space, and common symbols
    if (this.engravingMode === 'laser') {
      this.engravingRegex = /^[A-Za-z0-9 !@#$%&'()*+,\-./:;?_=]{1,6}$/;
    } else {
      // Standard monogram: 1-3 uppercase letters only
      this.engravingRegex = /^[A-Z]{1,3}$/;
    }

    // Bind events
    if (this.personalisationCheckbox) {
      this.personalisationCheckbox.addEventListener('change', (e) => {
        this.togglePersonalisation(e.target.checked);
      });
    }

    if (this.engravingInput) {
      // Uppercase transformation and validation on input
      this.engravingInput.addEventListener('input', (e) => {
        this.handleEngravingInput(e);
      });

      // Validate on blur
      this.engravingInput.addEventListener('blur', () => {
        this.validateEngraving();
      });
    }

    // Initial validation state (disabled by default)
    this.updateAddToCartState();
  }

  togglePersonalisation(isEnabled) {
    if (!this.personalisationContainer || !this.personalisationExpanded) return;

    if (isEnabled) {
      // Show expanded section
      this.personalisationExpanded.hidden = false;
      this.personalisationCheckbox.setAttribute('aria-expanded', 'true');
      this.personalisationContainer.setAttribute('data-personalisation-enabled', 'true');

      // Enable form inputs
      this.personalisationInputs.forEach(input => {
        input.disabled = false;
      });

      // Enable hidden "Personalisation: Yes" property
      if (this.personalisationHiddenInput) {
        this.personalisationHiddenInput.value = 'Yes';
        this.personalisationHiddenInput.disabled = false;
      }

      // Focus the engraving input
      if (this.engravingInput) {
        setTimeout(() => this.engravingInput.focus(), 100);
      }

    } else {
      // Hide expanded section
      this.personalisationExpanded.hidden = true;
      this.personalisationCheckbox.setAttribute('aria-expanded', 'false');
      this.personalisationContainer.setAttribute('data-personalisation-enabled', 'false');

      // Disable form inputs (won't be submitted)
      this.personalisationInputs.forEach(input => {
        input.disabled = true;
      });

      // Disable hidden property
      if (this.personalisationHiddenInput) {
        this.personalisationHiddenInput.disabled = true;
      }

      // Clear validation errors
      this.clearEngravingError();
    }

    // Update Add to Cart button state
    this.updateAddToCartState();
  }

  handleEngravingInput(e) {
    let value = e.target.value;

    // Transform to uppercase
    value = value.toUpperCase();

    // Filter characters based on mode
    if (this.engravingMode === 'laser') {
      // Allow letters, digits, space, and common symbols
      value = value.replace(/[^A-Za-z0-9 !@#$%&'()*+,\-./:;?_=]/g, '');
    } else {
      // Standard monogram: letters only
      value = value.replace(/[^A-Z]/g, '');
    }

    // Enforce max length (belt and suspenders with HTML maxlength)
    value = value.substring(0, this.engravingMax);

    // Update input value
    e.target.value = value;

    // Clear error when user types valid characters
    if (value.length > 0 && this.engravingRegex.test(value)) {
      this.clearEngravingError();
    }

    // Live validation feedback (non-intrusive)
    this.updateAddToCartState();
  }

  validateEngraving() {
    if (!this.engravingInput) return true;

    const isPersonalisationEnabled = this.personalisationCheckbox && this.personalisationCheckbox.checked;

    // Skip validation if personalisation is disabled
    if (!isPersonalisationEnabled) {
      this.clearEngravingError();
      return true;
    }

    const value = this.engravingInput.value.trim();

    // Validate: must be 1-3 uppercase letters
    const isValid = this.engravingRegex.test(value);

    if (!isValid) {
      // Show error
      this.showEngravingError();
    } else {
      this.clearEngravingError();
    }

    return isValid;
  }

  showEngravingError() {
    if (!this.engravingError || !this.engravingInput) return;

    this.engravingError.hidden = false;
    this.engravingInput.setAttribute('aria-invalid', 'true');

    // Add error styling class
    this.engravingInput.classList.add('product-custom__personalisation-input--error');
  }

  clearEngravingError() {
    if (!this.engravingError || !this.engravingInput) return;

    this.engravingError.hidden = true;
    this.engravingInput.removeAttribute('aria-invalid');
    this.engravingInput.classList.remove('product-custom__personalisation-input--error');
  }

  updateAddToCartState() {
    if (!this.submitButton) return;

    const isPersonalisationEnabled = this.personalisationCheckbox && this.personalisationCheckbox.checked;

    if (isPersonalisationEnabled) {
      // Check if engraving is valid
      const engravingValue = this.engravingInput ? this.engravingInput.value.trim() : '';

      // Disable button if:
      // 1. Engraving is empty OR
      // 2. Engraving doesn't match regex (1-3 letters A-Z)
      const shouldDisable = !engravingValue || !this.engravingRegex.test(engravingValue);

      if (shouldDisable) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('product-custom__submit-btn--disabled');
      } else {
        // Check if variant is available before enabling
        const isVariantAvailable = this.variantIdInput && this.variantIdInput.value;
        this.submitButton.disabled = !isVariantAvailable;
        this.submitButton.classList.remove('product-custom__submit-btn--disabled');
      }
    } else {
      // Personalisation not enabled, restore normal button state
      const isVariantAvailable = this.variantIdInput && this.variantIdInput.value;
      this.submitButton.disabled = !isVariantAvailable;
      this.submitButton.classList.remove('product-custom__submit-btn--disabled');
    }
  }

  /**
   * VARIANT SELECTOR
   */
  bindVariantEvents() {
    if (!this.variantInputs.length) return;

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateVariant();
      });
    });
  }

  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    if (variant) {
      this.updatePrice(variant);
      this.updateVariantId(variant);
      this.updateImage(variant);
      this.updateButtonState(variant);
    }
  }

  getSelectedOptions() {
    const options = [];
    this.variantInputs.forEach((input) => {
      if (input.type === 'radio') {
        if (input.checked) {
          const position = parseInt(input.dataset.optionPosition, 10);
          options[position - 1] = input.value;
        }
      } else if (input.tagName === 'SELECT') {
        const position = parseInt(input.dataset.optionPosition, 10);
        options[position - 1] = input.value;
      }
    });
    return options.filter(Boolean);
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) => {
      return selectedOptions.every((option, index) => {
        return variant[`option${index + 1}`] === option;
      });
    });
  }

  updatePrice(variant) {
    if (!this.priceElement) return;

    const priceAmount = this.priceElement.querySelector('.product-custom__price-amount');
    const priceCompare = this.priceElement.querySelector('.product-custom__price-compare');

    if (priceAmount) {
      priceAmount.textContent = this.formatMoney(variant.price);
    }

    if (priceCompare) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceCompare.innerHTML = `<s>${this.formatMoney(variant.compare_at_price)}</s>`;
        priceCompare.style.display = '';
      } else {
        priceCompare.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    // Simple AUD formatting - adjust currency symbol/format as needed
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars} AUD`;
  }

  updateVariantId(variant) {
    if (!this.variantIdInput) return;
    this.variantIdInput.value = variant.id;
    this.variantIdInput.disabled = !variant.available;
  }

  updateImage(variant) {
    if (!variant.featured_image || !this.mainImage) return;

    const newSrc = variant.featured_image.src;
    if (!newSrc) return;

    // Find and activate the matching thumbnail
    this.thumbnails.forEach((thumbnail) => {
      const thumbnailSrc = thumbnail.dataset.mediaSrc;
      if (thumbnailSrc && thumbnailSrc.includes(variant.featured_image.src.split('?')[0].split('/').pop())) {
        this.switchImage(thumbnail);
      }
    });
  }

  updateButtonState(variant) {
    if (!this.submitButton) return;

    const textSpan = this.submitButton.querySelector('.product-custom__submit-text');

    if (variant.available) {
      // Don't enable if personalisation validation fails
      this.updateAddToCartState(); // Will check personalisation validity
      if (textSpan && !this.submitButton.disabled) textSpan.textContent = 'Add to Cart';
    } else {
      this.submitButton.disabled = true;
      this.submitButton.classList.remove('product-custom__submit-btn--disabled');
      if (textSpan) textSpan.textContent = 'Sold Out';
    }
  }

  /**
   * QUANTITY SELECTOR
   */
  bindQuantityEvents() {
    if (!this.quantityInput || !this.minusBtn || !this.plusBtn) return;

    this.minusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value, 10) || 1;
      const minValue = parseInt(this.quantityInput.min, 10) || 1;
      if (currentValue > minValue) {
        this.quantityInput.value = currentValue - 1;
      }
    });

    this.plusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value, 10) || 1;
      const maxValue = parseInt(this.quantityInput.max, 10) || Infinity;
      if (currentValue < maxValue) {
        this.quantityInput.value = currentValue + 1;
      }
    });

    // Validate input on change
    this.quantityInput.addEventListener('change', () => {
      let value = parseInt(this.quantityInput.value, 10);
      const minValue = parseInt(this.quantityInput.min, 10) || 1;
      const maxValue = parseInt(this.quantityInput.max, 10) || Infinity;

      if (isNaN(value) || value < minValue) {
        value = minValue;
      } else if (value > maxValue) {
        value = maxValue;
      }

      this.quantityInput.value = value;
    });
  }

  /**
   * FORM SUBMISSION
   */
  bindFormSubmit() {
    const form = this.container.querySelector('[data-type="add-to-cart-form"]');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Validate variant is selected
      if (this.variantIdInput && !this.variantIdInput.value) {
        this.showError('Please select a variant');
        return;
      }

      // Validate personalisation if enabled
      const isPersonalisationEnabled = this.personalisationCheckbox && this.personalisationCheckbox.checked;
      if (isPersonalisationEnabled) {
        if (!this.validateEngraving()) {
          this.showError('Please enter valid engraving text (1-3 letters)');
          return;
        }
      }

      // Show loading state
      this.setLoadingState(true);

      try {
        const formData = new FormData(form);

        // If personalisation is NOT enabled, remove the properties from formData to avoid cluttering the cart
        if (!isPersonalisationEnabled) {
          // FormData entries cannot be easily deleted in all browsers, so we reconstruct
          // or just ensure the inputs are disabled (which we do in togglePersonalisation)
          // Double check inputs are disabled:
          this.personalisationInputs.forEach(input => input.disabled = true);
          if (this.personalisationHiddenInput) this.personalisationHiddenInput.disabled = true;
        }

        const config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          },
          body: formData
        };

        const response = await fetch(`${routes.cart_add_url || '/cart/add.js'}`, config);
        const responseJson = await response.json();

        if (response.ok) {
          // Redirect to cart
          window.location.href = '/cart';
        } else {
          this.showError(responseJson.description || 'Failed to add item to cart');
          this.setLoadingState(false);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError('An unexpected error occurred. Please try again.');
        this.setLoadingState(false);
      }
    });
  }

  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.classList.add('loading');
      this.submitButton.disabled = true;
    } else {
      this.submitButton.classList.remove('loading');
      this.submitButton.disabled = false;
    }
  }

  showError(message) {
    const errorWrapper = this.container.querySelector('.product-form__error-message-wrapper');
    const errorMessage = this.container.querySelector('.product-form__error-message');

    if (errorWrapper && errorMessage) {
      errorMessage.textContent = message;
      errorWrapper.hidden = false;

      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorWrapper.hidden = true;
      }, 5000);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const productContainers = document.querySelectorAll('[id^="ProductCustom-"]');
  productContainers.forEach((container) => {
    new ProductCustom(container);
  });
});

// Re-initialize on section load (Theme Editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[id^="ProductCustom-"]');
    if (container) {
      new ProductCustom(container);
    }
  });
}
